// Runtime manifest loader for optional compile-mode and execution overrides.
// Example: data-luis-manifest="/lenty-hybrid.json" with suffix rules.
import type { LuisSourceElement } from '../context'
import { readLuisManifestAttribute } from '../options'
import type { LuisManifestResolution, ManifestCacheEntry } from './manifest-types'
import { buildManifestExecutionResolver, buildManifestModeResolver, buildManifestWarning, validateManifestDocument } from './manifest-validate'
import { isAllowedManifestUrl } from '@lentystyle/guard/runtime'

export type { LuisManifestResolution } from './manifest-types'

const manifestCache = new Map<string, Promise<ManifestCacheEntry>>()

// Resolve manifest mode/execution resolvers from one source element attribute.
export async function resolveManifestModeResolver(source: LuisSourceElement): Promise<LuisManifestResolution> {
  const manifestAttribute = readLuisManifestAttribute(source)
  // Return empty manifest payload when no attribute is defined.
  if (!manifestAttribute) {
    return { warnings: [] }
  } else {
    // Continue manifest href resolution.
  }

  const manifestUrl = resolveManifestUrl(manifestAttribute, source)
  // Return warning when manifest url cannot be resolved.
  if (!manifestUrl) {
    return {
      warnings: [buildManifestWarning(`Invalid data-luis-manifest URL: ${manifestAttribute}`)],
    }
  } else {
    // Continue manifest loading for resolved absolute url.
  }

  const loaded = await loadManifestDocument(manifestUrl)
  // Return warnings when manifest cannot be parsed/validated.
  if (!loaded.document) {
    return {
      manifestUrl,
      warnings: loaded.warnings,
    }
  } else {
    // Continue resolver creation for valid manifest document.
  }

  // Return cached manifest resolvers and validation warnings.
  return {
    manifestUrl,
    modeOverrideResolver: buildManifestModeResolver(loaded.document.rules),
    executionOverrideResolver: buildManifestExecutionResolver(loaded.document.rules),
    warnings: loaded.warnings,
  }
}

// Resolve manifest URL relative to source/document base.
function resolveManifestUrl(value: string, source: LuisSourceElement): string | null {
  const href = value.trim()
  // Reject empty manifest urls after trim normalization.
  if (!href) {
    return null
  } else {
    // Continue base resolution for non-empty href.
  }

  try {
    const base = resolveSourceBaseHref(source)
    // Reject manifest URL when runtime policy blocks this source origin/scheme.
    if (!isAllowedManifestUrl(href, base)) {
      return null
    } else {
      // Continue absolute URL resolution for allowed manifest URL.
    }
    // Return absolute manifest URL for stable cache keys.
    return new URL(href, base).href
  } catch {
    // Return null when URL constructor cannot resolve href/base.
    return null
  }
}

// Resolve source base href used for relative manifest URLs.
function resolveSourceBaseHref(source: LuisSourceElement): string {
  if (source instanceof HTMLLinkElement) {
    return source.href
  } else if (typeof document !== 'undefined' && typeof document.baseURI === 'string') {
    // Use document.baseURI for inline style source nodes.
    return document.baseURI
  } else if (typeof window !== 'undefined' && window.location?.href) {
    // Fallback to browser location href when baseURI is unavailable.
    return window.location.href
  } else {
    // Return root-like fallback base for non-browser environments.
    return '/'
  }
}

// Load and cache manifest document by absolute URL.
async function loadManifestDocument(manifestUrl: string): Promise<ManifestCacheEntry> {
  const cached = manifestCache.get(manifestUrl)
  // Return in-flight cache promise when manifest was already requested.
  if (cached) {
    return cached
  } else {
    // Continue fresh manifest fetch and validation.
  }

  const nextPromise = fetchAndParseManifest(manifestUrl)
  manifestCache.set(manifestUrl, nextPromise)
  // Return freshly created cache promise.
  return nextPromise
}

// Fetch manifest JSON and validate minimal schema.
async function fetchAndParseManifest(manifestUrl: string): Promise<ManifestCacheEntry> {
  // Reject blocked manifest URLs before network fetch starts.
  if (!isAllowedManifestUrl(manifestUrl, manifestUrl)) {
    return {
      document: null,
      warnings: [buildManifestWarning(`[${manifestUrl}] Manifest URL blocked by runtime security policy.`)],
    }
  } else {
    // Continue manifest fetch for policy-allowed URL.
  }

  try {
    const response = await fetch(manifestUrl)
    // Return warning payload for non-2xx responses.
    if (!response.ok) {
      return {
        document: null,
        warnings: [buildManifestWarning(`[${manifestUrl}] Unable to load manifest (${response.status}).`)],
      }
    } else {
      // Continue reading response body for valid status.
    }

    const raw = await response.text()
    const parsed = JSON.parse(raw) as unknown
    // Return validated manifest payload with optional warnings.
    return validateManifestDocument(parsed, manifestUrl)
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown manifest error.'
    // Return warning payload for fetch/parse exceptions.
    return {
      document: null,
      warnings: [buildManifestWarning(`[${manifestUrl}] Manifest parsing failed: ${message}`)],
    }
  }
}
